/**
 * Unapplied credit the biller should be told about before collecting
 * more money (gdn-3175).
 *
 * "Deal with the credit first" is genuinely correct bookkeeping — taking
 * a fresh payment while the patient is already in credit manufactures a
 * refund. The review state used to enforce that by hiding the collect
 * verb, which carried the advice by removing the capability. This
 * carries the advice instead.
 */
export interface CreditSuggestion {
  /** What an allocation verb can actually move. */
  allocatableCents: number;
  /**
   * Whether any visit can receive it — one with a confirmed amount still
   * owed, not on hold (`creditTargetsOf`). Without one the prompt must not offer
   * "Apply credit": the dialog would open on an empty list with a button
   * that can never enable, which is the dead end Island Life reported
   * (gdn-4176, IL01). The credit is still worth mentioning; it just has
   * nowhere to go yet.
   */
  hasTarget: boolean;
}

/**
 * @param allocatableCents the summary's `account_allocatable_credit_cents`.
 *
 * Absent for two different reasons, both correctly absent:
 *   - `0`    — there is credit machinery but nothing left to move.
 *   - `null` — an older BE that does not report an allocatable figure at
 *              all, so no allocation verb exists to offer.
 *
 * The second is why this never falls back to a gross credit figure:
 * suggesting "apply it first" for credit that no verb can move sends the
 * biller to a dead end, which is worse than saying nothing.
 *
 * @param hasTarget whether `creditTargetsOf` finds any visit over the same
 *   financial summary the Apply credit dialog reads — the same rule, not a
 *   second copy of it.
 */
export const creditSuggestionOf = (
  allocatableCents: number | null | undefined,
  hasTarget: boolean,
): CreditSuggestion | null =>
  typeof allocatableCents === 'number' && allocatableCents > 0
    ? { allocatableCents, hasTarget }
    : null;
